import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Column } from 'src/components/table';
import { RowsPerPageOptions } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import {
  DEFAULT_POSITION_ALL,
  LISTVIEW_MAX_ROWS_PER_PAGE,
} from 'src/constants';
import { LocationSchedulePageTable } from 'src/features/locations/scheduleLocationGroupPage/modules/LocationSchedulePageTable/LocationSchedulePageTable';
import {
  useGetAllDayparts,
  useGetDaypartsByLocationGroup,
  useGetLocationGroup,
  useGetLocationsCountByGroup,
  useGetPaginatedDefaultAssemblies,
  useGetProjectConfig,
} from 'src/hooks';
import { useDeleteScheduleOrItems } from 'src/hooks/api/schedules/useDeleteScheduleOrItems';
import { useGetPaginatedSchedules } from 'src/hooks/api/schedules/useGetAllSchedules';
import { useApiErrorNavigator } from 'src/hooks/navigation/useApiErrorNavigator';
import { IDaypart } from 'src/interfaces';
import { formatDate, lang } from 'src/utils';
import { formatTimestamp } from 'src/utils/date';

import { DeleteBundleAction } from '../calendarLocationGroupPage/modules/CalendarView/eventContentSidebar/EventContentSidebar.interface';
import { VIEW_OPTIONS } from './modules/LocationSchedulePageTable/LocationSchedulePageTable.interface';

const columnsDefaultAssembly = [
  {
    id: 'startDate',
    sortKey: 'start_date',
    label: lang.startDate,
    sortIcon: true,
  },
  {
    id: 'endDate',
    sortKey: 'valid_to',
    label: lang.endDate,
    sortIcon: true,
  },
  {
    id: 'assemblyName',
    label: lang.assemblyName,
    sortIcon: false,
  },
  {
    id: 'status',
    label: lang.status,
    sortIcon: false,
  },
  {
    id: 'assemblyScheduleDate',
    sortKey: 'modified_at',
    label: lang.modifiedDate,
    sortIcon: true,
  },
];

const columnsScheduleComponent = [
  {
    id: 'name',
    sortKey: 'name',
    label: lang.scheduleComponent.scheduleNameField,
    sortIcon: true,
    width: 'calc(100% - 440px)',
  },

  {
    id: 'modified_at',
    sortKey: 'modified_at',
    label: lang.modifiedDate,
    sortIcon: true,
    width: '200px',
    align: 'right',
  },
];

const columnsScheduleComponentExpanded: Column[] = [
  {
    id: 'start_date',
    sortKey: 'start_date',
    label: lang.startDate,
    sortIcon: true,
    width: '120px',
  },
  {
    id: 'end_date',
    sortKey: 'end_date',
    label: lang.endDate,
    sortIcon: true,
    width: '120px',
  },
  {
    id: 'name',
    sortKey: 'name',
    label: 'Component name',
    sortIcon: true,
    width: 'calc(100% - 240px)',
  },
  {
    id: 'override',
    label: 'Override enabled',
    sortIcon: false,
  },
];

export const ScheduleLocationGroupPage = () => {
  const { locationGroupId = 0 } = useParams();
  const { data: locationsCount = 1 } = useGetLocationsCountByGroup(
    [`locationGroups.id=${locationGroupId}`],
    Boolean(locationGroupId),
  );
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    daypartId: 1,
    position: DEFAULT_POSITION_ALL,
  });
  const [rowsPerPage, setRowsPerPage] = useState(LISTVIEW_MAX_ROWS_PER_PAGE);
  const [selectedOption, setSelectedOption] = useState<string>(
    VIEW_OPTIONS.SCHEDULE_COMPONENT,
  );
  const isDefaultAssembly = selectedOption === VIEW_OPTIONS.DEFAULT_ASSEMBLY;
  const { data: projectConfig } = useGetProjectConfig();

  const [sortKey, setSortKey] = useState<string>('modified_at');
  const [sortDirection, setSortDirection] = useState<string>('descending');
  const [sortSelected, setSortSelected] = useState(false);

  const sortParam = `${sortDirection === 'descending' ? '-' : ''}${sortKey}`;

  const { data: daypartOptions, isLoading: isLoadingDayparts } =
    useGetAllDayparts();

  const shouldFetchAll = ![
    RowsPerPageOptions.TWENTY,
    RowsPerPageOptions.FIFTY,
    RowsPerPageOptions.ONE_HUNDRED,
  ].includes(rowsPerPage);

  const {
    data: {
      defaultAssemblyData = [],
      totalPages = 1,
      totalCount: totalCountDefault = 1,
    } = {},
    isLoading: isLoadingDefaultAssemblies,
    refetch: refetchSchedules,
  } = useGetPaginatedDefaultAssemblies(
    [
      `location_group_id=${locationGroupId}`,
      `sort=${sortParam}`,
      `page=${page}`,
      `daypart_id=${filters.daypartId}`,
      `pageSize=${shouldFetchAll ? 0 : rowsPerPage}`,
      `currentDate=${dayjs().format('YYYY-MM-DD')}`,
    ],
    Boolean(locationGroupId) && isDefaultAssembly,
  );
  const {
    data: {
      schedulesData = [],
      totalSchedulesPages = 1,
      totalCount: totalCountSchedule = 1,
    } = {},
    isLoading: isLoadingSchedules,
  } = useGetPaginatedSchedules(
    [
      `location_group_id=${locationGroupId}`,
      `sort=${sortParam}`,
      `page=${page}`,
      `daypart_id=${filters.daypartId}`,
      `position=${filters.position}`,
      `pageSize=${rowsPerPage}`,
    ],
    Boolean(locationGroupId) && !isDefaultAssembly,
    shouldFetchAll,
  );
  const {
    data: activeDaypartOptions = [],
    isLoading: isLoadingActiveDayparts,
  } = useGetDaypartsByLocationGroup(
    [`location_group_id=${locationGroupId}`, 'valid_to.null=true'],
    Boolean(locationGroupId),
  );
  const { data: locationGroup, error } = useGetLocationGroup(
    locationGroupId.toString(),
  );
  const deleteScheduleComponents = useDeleteScheduleOrItems();
  const isSelectedDaypartActive = activeDaypartOptions.some(
    (daypart) => daypart.id === Number(filters.daypartId),
  );
  useApiErrorNavigator(error);
  useEffect(() => {
    setPage(1);
    setFilters({
      daypartId: 1,
      position: DEFAULT_POSITION_ALL,
    });
  }, [selectedOption]);
  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
    setPage(1);
  };
  const formattedSchedulesData = (schedulesData || []).map((item) => ({
    ...item,
    start_date: formatDate(item.start_date as string),
    end_date: formatDate(item.end_date as string),
    modified_at: formatTimestamp(item?.modified_at || '') || '',
    scheduleItems: (item.scheduleItems || []).map((item) => ({
      ...item,
      start_date: formatDate(item.start_date),
      end_date: formatDate(item.end_date as string),
      modified_at: '',
    })),
  }));

  const handleDaypartChange = (newDaypartId: number) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, daypartId: newDaypartId }));
  };
  const handlePositionChange = (newPositionId: string) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, position: newPositionId }));
  };
  const handleSortingClick = (columnId: string, direction: string) => {
    const selectedColumns = isDefaultAssembly
      ? columnsDefaultAssembly
      : columnsScheduleComponent;
    const column = selectedColumns.find((col) => col.id === columnId);

    if (!column?.sortKey) return;

    setSortKey(column.sortKey);
    setSortDirection(direction);
    setPage(1);
    setSortSelected(true);
  };
  const shouldShowListview = {
    daypartDisabled:
      !isLoadingActiveDayparts && activeDaypartOptions.length === 0,
    noDefaultAssembly:
      !isLoadingActiveDayparts &&
      !isLoadingDefaultAssemblies &&
      isDefaultAssembly &&
      activeDaypartOptions.length > 0 &&
      defaultAssemblyData.length === 0,
    noScheduleComponent:
      !isLoadingActiveDayparts &&
      !isLoadingSchedules &&
      !isDefaultAssembly &&
      activeDaypartOptions.length > 0 &&
      schedulesData.length === 0,
    previewDisable: !isSelectedDaypartActive,
  };

  return (
    <LocationSchedulePageTable
      columns={
        isDefaultAssembly ? columnsDefaultAssembly : columnsScheduleComponent
      }
      expandedColumns={
        isDefaultAssembly ? undefined : columnsScheduleComponentExpanded
      }
      tableData={
        isDefaultAssembly ? defaultAssemblyData : formattedSchedulesData
      }
      locationGroup={locationGroup}
      locationGroupScheduleLoading={
        isDefaultAssembly ? isLoadingDefaultAssemblies : isLoadingSchedules
      }
      totalCount={isDefaultAssembly ? totalCountDefault : totalCountSchedule}
      refetchSchedules={refetchSchedules}
      isLocationGroupSchedule={true}
      page={page}
      totalPage={isDefaultAssembly ? totalPages : totalSchedulesPages ?? 1}
      onPageChange={(_, newPage) => setPage(newPage)}
      selectedDaypartId={filters.daypartId}
      selectedPositionId={filters.position}
      daypartOptions={daypartOptions as IDaypart[]}
      isLoadingDayparts={isLoadingDayparts}
      onDaypartChange={handleDaypartChange}
      onPositionChange={handlePositionChange}
      selectedOption={selectedOption}
      onSelectedOptionChange={(newSelectedOption: string) => {
        setSelectedOption(newSelectedOption);
        setSortSelected(false);
      }}
      projectConfig={projectConfig}
      sortDirection={{
        key: sortKey,
        direction: sortDirection,
        isSortModified: sortSelected,
      }}
      handleSortingClick={handleSortingClick}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleRowsPerPageChange}
      deleteBundleAction={deleteScheduleComponents as DeleteBundleAction}
      locationsCount={locationsCount}
      shouldShowListview={shouldShowListview}
    />
  );
};
